import { gameNamesDesktop } from "abstract/constants/game-ids"
import { ConsentRow } from "components/userConsentEditor"
import { NotificationsPerGame, SpecialEvent } from "domains/specialEvents/pages/main"

export const configItemTypes = {
  iframes: "iframes",
  buttons: "buttons",
  contests: "contests",
  avatar: "avatar",
  texts: "texts",
  navbarAvatarTabs: "navbarAvatarTabs",
  positionsIngameScreens: "positionsIngameScreens",
  dynamicUI: "dynamicUI",
  inGameBanners: "inGameBanners",
  adsBanners: "adsBanners",
  mobileConfig: "mobileConfig",
  blockedGameModes: "blockedGameModes",
  blockedGameModesV2: "blockedGameModesV2",
  specialEvents: "specialEvents",
  variousConfig: "variousConfig",
  offerwallSettings: "offerwallSettings",
  monitoringConfig: "monitoringConfig",
  analyticsConfig: "analyticsConfig",
  socketsConfig: "socketsConfig",
  featureFlags: "featureFlags",
  recordingConfig: "recordingConfig",
  specialEventPresets: "specialEventPresets",
} as const

export type NameItem = {
  type: ConfigItemType;
  oldName: string;
  newName?: string;
};

export type TypeItem = {
  oldType: ConfigItemType;
  newType?: ConfigItemType;
};

export type ConfigItemAtList<K extends ConfigItemType = ConfigItemType> = ConfigItem<K> & {
  createdAt: Date;
  updatedAt: Date;
};

export type ActionType = "iframe" | "external-link" | "internal-routing";

export type ContestConfigColors = {
  primary: { default: string; light: string; dark: string };
  secondary: { default: string; hover: string; click: string };
  background: { main: string; secondary: string };
  neutral: { inactive: string; black: string; gray: string; red: string };
  text: { inactive: string; white: string; gray: string };
};
export type ContestConfigOnboardingScreenData = {
  title: string;
  text: string;
  image: string;
  consentRows?: ConsentRow[];
};
export type ContestConfigSchedulePageData = {
  image: string;
  title: string;
  text: string;
};

export type ContestConfigLiveData = {
  image: string;
  periodName?: string;
  title: string;
  text: string;
  startDate: Date;
  endDate: Date;
};
export type FreePointType = "share" | "invite" | "newsletter";

export type ContestConfigFreePointsData = {
  type: FreePointType;
  title: string;
  rewardText: string;
  points: number;
  text: string;
  image: string;
  watermark?: string;
  watermarkWidth?: number;
  watermarkPosition?: "center" | "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

  CTA: { activeText: string; inactiveText: string };
  topText?: string;
  highlightsScreen?: {
    header: string;
    description: string;
    CTAtext: string;
  };
  maxFreePoints?: number;
  goalInvite?: number;
};
export type ContestConfigStartgameWindowData = {
  contestImage: string;
};
export type ContestConfigEndgameWindowData = {
  endgameSuccessImage: string;
  endgameFailImage: string;
  endgameFraudImage: string;
  gameBackgroundImage: string;
};
export type RulesSectionType = {
  buttonText?: string;
  header?: string;
  paragraps?: string;
};
export type LoungePageType = {
  loungeSmallImg?: string;
  loungeLargeImg?: string;
  games?: string[];
};

export type TranslationConfig = {
  countryTranslations?: { [key: string]: {[key: string]: string} };
  splashScreen?: {
    title?: string;
    subtitle?: string;
    CTAtext?: string;
    TOCtext?: string;
  };
  onboardingCTA?: {
    continueButton?: string;
    joinButton?: string;
    TOCtext?: string;
  };
  errorStateGlobal?: {
    title?: string;
    subtitle?: string;
    CTAtext?: string;
  };
  schedule?: {
    bigTimerTitle?: string;
    smallTimerTitle?: string;
    getStartedButton?: string;
    tabs?: {
      schedule?: string;
      leaderboard?: string;
      freePoints?: string;
      myRewards?: string;
    };
  };
  leaderboard?: {
    head?: {
      rank?: string;
      nickname?: string;
      points?: string;
      pointsTooltip?: string;
    };
    emptyPage?: {
      title?: string;
      subtitle?: string;
    };
  };
  rewards?: {
    head?: {
      date?: string;
      event?: string;
      reward?: string;
    };
    claimButton?: string;
    claimedButton?: string;
    supportButton?: string;
    faqButton?: string;
    footerText?: string;
    footerAsterixNote?: string;
    emptyPage?: {
      title?: string;
      subtitle?: string;
    };
  };
  invitePopup?: {
    header?: string;
    body?: string;
    disclaimer?: string;
    inviteesLeft?: string;
    completedInvitees?: string;
  };
  tooltipData?: {
    twitterSharingText: string;
    reward?: string;
  };
  rulesSection?: RulesSectionType;
  challengesTranslations?: {
    progressNotFinished?: string;
    progressBuffsGone?: string;
    progressAllDone?: string;
    progressPrize?: string;
    challengesBlockDescription?: string;
  };
};
export type ITopLadderRewardConfiguration = {
  type?: "BUFF_POINTS" | "GIFT_CARDS";
  rewardText?: string;
  buffPointsAmount?: number;
  giftCardText?: string;
  name?: string;
  transactionName?: string;
};
export type GrandPrizeReward = {
  tooltipText: string
};

export type GrandPrizeRewards = {
  [gameId: string]: GrandPrizeReward[]
}

export type ContestGameOption = {
  gameId: string;
  mapIdentifiers?: string[];
};

export type ContestConfig = {
  prettyName?: string;
  topBarLogo?: string;
  contestType?: "CHALLENGE_BASED" | "SCORE_BASED";
  mainLogo?: string;
  navigationLogo?: string;
  settingsLogo?: string;
  advImage?: string;
  gameTitleImage?: string;
  gameTitleImageURL?: string;
  gameTitleImageWinnersList?: string;
  gameTitleImageWinnersListURL?: string;
  isMappedRaffleMode?: boolean;

  countries?: string[];
  services?: string[];
  analyticsImpressionInterval?: number;
  termsAndConditionsLink?: string;
  supportLink?: string;
  faqLink?: string;
  startDate?: Date;
  endDate?: Date;
  colors?: ContestConfigColors;
  distributionType?: "EVERY_WEEK" | "FINAL_WEEK_ONLY" | "ALL-TIME_LEADERBOARD";

  onboarding?: ContestConfigOnboardingScreenData[];

  contestLiveSchedule?: ContestConfigLiveData[];
  contestNotStartedSchedule?: ContestConfigSchedulePageData;
  contestFinishedSchedule?: ContestConfigSchedulePageData & {
    CTAtext: string;
  };
  gameProducerDisclaimer?: string;

  pointAwarding?: PointAwarding;
  contestServerSideConfiguration?: ContestServerSideConfiguration;
  freePoints?: ContestConfigFreePointsData[];
  startgameWindow?: ContestConfigStartgameWindowData;
  endgameWindow?: ContestConfigEndgameWindowData;

  loungePageData?: LoungePageType;
  // games?: string[];
  leaderboardBanner?: string;
  leaderboardTopBanner?: string;
  leaderboardTopBannerCountry?: { [key: string]: string };
  leaderboardAdReplacementBanner?: string;
  leaderboardBannerCountry?: { [key: string]: string };
  leaderboardWithCountry?: boolean;
  leaderboardHasGlobal?: boolean;
  leaderboardBanners?: {
    topBannerCopyText?: string;
    rightBannerCopyText?: string;
  }
  displayAds?: boolean;
  topLadderRewardStrategy?: ITopLadderRewardConfiguration;

  inGameNotificationsRedefine?: boolean;
  // Record<'gameId', Record<'notification-name', ingameNotificationType>>
  notificationsPerGame?: NotificationsPerGame[];
  challengeBasedContest?: {
    topMainPageLeftBanner?: string;
    topMainPageRightBanner?: string;
    rightMainPageBanner?: {
      imageNotCompleted1State: string;
      imageCompleted1State?: string;
      text1State?: string;
      title1State?: string;
      CTAtext1State?: string;

      imageNotCompleteState?: string;
      titleNotCompleteState?: string;
      textNotCompleteState?: string;
      CTAtextNotCompleteState?: string;

      imageGrandState?: string;
      titleGrandState?: string;
      textGrandState?: string;
      CTAtextGrandState?: string;
    };
    leaderboard?: {
      notwin?: ContestConfigSchedulePageData & { CTAtext?: string };
      win?: ContestConfigSchedulePageData & { CTAtext?: string };
      right?: ContestConfigSchedulePageData & { titleFinished?: string };
    };
    progressBlockImage?: string;
    winnersListImageBottom?: string;
    winnersListImageBottomURL?: string;
    winnersListImageTable?: string;
    winnersListImageTableURL?: string;
  };
  grandPrizeRewards?: GrandPrizeRewards
  scheduleSidebarBannerImage?: string
  leaderboardFullheightSidebar?: boolean
  highlightSharing?: boolean
  showAllTimeLeaderboard?: boolean
  recurringNotificationsInterval?: number | null;
  inAppPixels?: Pixel[]
  startgamePixels?: Pixel[]
  endgamePixels?: Pixel[]
  disableChallengesOnStartgameScreen?: boolean
  automaticHighlightRecordingEnabling?: boolean
  disclaimer?: string
  howItWorksTabEnabled?: boolean
  howItWorksContent?: string
  ignoreAdsHiding?: boolean;
  gamesOptions?: ContestGameOption[];
} & TranslationConfig;

export type Pixel = {
  src: string;
  firingInterval?: number;
}

export type PixelEntry = Partial<Pixel> | string | null | undefined;

export interface ConfigWithNameType {
  newName?: string;
  newType?: ConfigItemType;
}

export enum SecondaryWindowPositions {
  TOP_LEFT = "TOP_LEFT",
  TOP_CENTER = "TOP_CENTER",
  TOP_RIGHT = "TOP_RIGHT",
  CENTER_LEFT = "CENTER_LEFT",
  CENTER = "CENTER",
  CENTER_RIGHT = "CENTER_RIGHT",
  BOTTOM_LEFT = "BOTTOM_LEFT",
  BOTTOM_CENTER = "BOTTOM_CENTER",
  BOTTOM_RIGHT = "BOTTOM_RIGHT",
}
export enum SecondaryWindowTitle {
  startgame = "startgame",
  endgame = "endgame",
  launchGame = "launchGame",
  secondMonitor = "secondMonitor",
  gameSummary = "gameSummary",
}

export type PositionsIngameScreens = Record<
  keyof typeof gameNamesDesktop,
  Record<SecondaryWindowTitle, SecondaryWindowPositions>
>;

export interface BlockedGameModesV2Config {
  blockedGameModes?: {
    gameId: string,
    exactMatches: string[],
    partialMatches: string[]
  }[]
}

export interface AvatarConfig {
  clipRecording?: boolean;
}

export interface AvatarNavbarTabsConfig {
  discover?: { queryName: string; tabName: string }[];
  highlights?: { queryName: string; tabName: string }[];
}

export interface TextsConfig {
  texts?: { title: string; subtitle: string }[];
}

export interface IFrameSettings {
  tokenRequired?: boolean;
  withBg?: boolean;
  withAd?: boolean;
  withScrollbar?: boolean;
  outsideClickHandler?: boolean;
  title?: string;
  securityGroup?: string;
  url?: string;
  services?: string[];
  dimensions?: {
    width: number;
    height: number;
  };
  position?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  };
}

export interface IFrameButtonsConfig {
  text?: string;
  icon?: string;
  queries?: Record<string, string>;
  action?: ActionType;
  href?: string;
  to?: string;
  iframeName?: string;
}

export interface CommonDynamicUI {
  underMaintenance?: {
    BUFF_DESKTOP?: boolean;
    BUFF_PLAY?: boolean;
    BUFF_MOBILE?: boolean;
  }
}

export interface MobileDynamicUI {
  mobileTopbarMask?: string;
  mobileMarketplaceItemMask?: string;
  mobileSidebarMask?: string;
}

export interface DesktopDynamicUI {
  topbarSettingsImage?: string;
  topbarSettingsMainColor?: string;
  topbarSettingsSecondColor?: string;
  loungeVariousTaskImage?: string;
  avatarMasksImages?: string[];
  cursorImage?: string;
  cursorImagePointer?: string;
  wheelSpinImage?: string;
  wheelSpinImagePaid?: string;
  wheelSpinGuideTextFree?: string;
  wheelSpinGuideTextPaid?: string;
  marketMaskImage?: string;
  backgroundImage?: string;
  backgroundImagePopups?: string;
  marketGeoValueImage?: string;
  marketGeoValueString?: string;
  challengeBannerValueImage?: string;
  challengeBannerCTA?: string;
  buffPayILState?: boolean;
  buffPayILCTA?: string;
  buffPayILValueImage?: string;
  fallAnimation?: boolean;
  fallAnimationImage?: string;
  fallAnimationImageHeight?: string;
  fallAnimationImageCount?: string;
  inviteFriendsRules?: string;
  inviteFriendsTooltipData?: string[];
  paypalOnButtonState?: boolean;
  ingameScreenAdType?: "short" | "tall";
  buffboxImagesLinks?: { openedBox: string, closedBox: string }
  buffboxBackdropImage?: string;
  buffboxBackgroundGlowingBlobs?: string;
  startgameScreenType?: "window" | "notification";
  enableLoggedOutGameScreens?: boolean;
  // Per-screen kill switch for second-monitor routing; keys match buff.app GameScreenName.
  secondMonitorScreens?: {
    launchGame?: boolean;
    startgame?: boolean;
    endgame?: boolean;
    gameSummary?: boolean;
  };
  // Launch screens render only for these countries (uppercase ISO-2).
  // Absent/empty = show everywhere; unknown user country = don't show.
  launchScreenCountryAllowlist?: string[];
  // DEV2-1909/1912: friends feature renders only for countries in this allowlist (uppercase ISO-2).
  // Absent/empty = feature off (dark by default). ["*"] = feature on for everyone.
  friendsCountryAllowlist?: string[];
}

export type DynamicUIType = CommonDynamicUI & MobileDynamicUI & DesktopDynamicUI;

export type MobileConfigTab = {
  src: string;
  icon: string;
  name: string;
  iconProps?: { [key: string]: any };
  uid: string;
};

export type MobileConfigHeaderButton = {
  src: string;
  icon: string;
  options?: { [key: string]: any };
  uid: string;
};

export type MobileConfigAnalytics =
  | {
      [key: string]: any;
    }
  | undefined;

export type MobileConfigVariant = {
  tabs: MobileConfigTab[];
  headerButtons?: MobileConfigHeaderButton[];
  analytics?: MobileConfigAnalytics;
};

export type MobileConfigType = {
  variants?: MobileConfigVariant[];
};

export type VariousConfigType = {
  codStartgameAutoclose?: number;
  codLaunchgameAutoclose?: number;
  codEndgameAutoclose?: number;
  codGameSummaryAutoclose?: number;
  // Hard auto-close for ALL launch screens, seconds. Absent = client default 20; 0 disables.
  launchgameAutoclose?: number;
  tropicanaNightperiodStartHour?: number;
  tropicanaNightperiodEndHour?: number;
  tropicanaTrackingResetTimeHour?: number;
}

export type MonitoringConfigType = {
  monitoringSampleRate?: number;
  blacklist?: {
    pattern: string;
    matchType: "exact" | "partial" | "regex";
  }[]
  emailWhitelist?: string[]
  minimumSupportedVersion?: string;
  countryBlacklist?: string[];
}

export type SpecialEventsConfigType = {
  specialEvents?: SpecialEvent[]
}

export type SpecialEventPresetSource = "code" | "code-and-sheet" | "manual";

export type SpecialEventPresetAssetExpectation = {
  required?: boolean;
  scope?: "global";
  aliases?: string[];
}

export type SpecialEventPresetNotificationDefaults = {
  enabled?: boolean;
  spawnProbability?: number;
  ignoreSpawnCap?: boolean;
  ingameOverlayType?: "stacking" | "generic";
  buffsReward?: number;
  duration?: number;
  triggerDelay?: number;
  cooldown?: number;
  playSound?: boolean;
  customStyles?: string | Record<string, unknown>;
}

export type SpecialEventNotificationPreset = {
  triggerEventName: string;
  friendlyName: string;
  description?: string;
  recommendationScore?: string;
  emotion?: string;
  comment?: string;
  source?: SpecialEventPresetSource;
  codeSupported?: boolean;
  trackingName?: string;
  aliases?: string[];
  asset?: SpecialEventPresetAssetExpectation;
  defaults?: SpecialEventPresetNotificationDefaults;
}

export type SpecialEventGamePreset = {
  gameId: number;
  gameName: string;
  trackingName?: string;
  aliases?: string[];
  events: SpecialEventNotificationPreset[];
}

export type SpecialEventPresetsConfigType = {
  version: number;
  generatedFrom?: string[];
  defaultStyle?: Record<string, unknown>;
  defaultNotification?: SpecialEventPresetNotificationDefaults;
  games: Record<string, SpecialEventGamePreset>;

  /**
   * Offered for any game id absent from `games`. Time-based titles have no GEP feed and there are
   * thousands of them, so they cannot be enumerated per game.
   */
  timeBasedEvents?: SpecialEventNotificationPreset[];
}

export type OfferwallSettings = {
  whitelistedCountries?: string[];
}

export const userTypes = ["ALL", "PREMIUM", "REGULAR"] as const

export const windowTypes = [
  "MAIN_SCREEN",
  "START_GAME",
  "END_GAME",
  "LAUNCH_GAME",
  "GAME_SUMMURY",
  "SECOND_MONITOR",
] as const
export type windowTypesType = typeof windowTypes[number];

export type InGameBannerType = {
  name?: string;
  countries?: string[];
  gameIds?: string[];
  userType?: typeof userTypes[number];
  link?: string;
  windows?: windowTypesType[];
  startDate?: Date;
  endDate?: Date;
  image?: string;
  ignoreAdsHiding?: boolean;
  clickable?: boolean;
  pixels?: Pixel[];
};

export type Response = {
  success: boolean;
  message: string;
  statusCode: number;
};

export interface ConfigItemResponse<T extends ConfigItem = ConfigItem> extends Response {
  item: T["data"];
}

export type ContestServerSideConfiguration = {
  contestTwitterShareMessage: string;
  contestRewardCount: number;
  contestSharingInterval?: number;
};

export type PointAwarding = {
    maxpointsTime: number;
    maxpoints: number;
    secondsToLosePoint: number;
    multiplayerFactor: number;
}

export type AnalyticsConfigType = {
  analyticsSampleRate: number;
  analyticsWhitelistedCountries?: string[];
}

export const BP2InAppReviewConfigTriggerEnum = {
  FirstDailyBonusClaim: "first_daily_bonus_claim",
  BuffPointsReward: "buff_points_reward",
  SuccessfulRedeem: "successful_redeem",
} as const

export type BP2InAppReviewConfigTriggerType = (typeof BP2InAppReviewConfigTriggerEnum)[keyof typeof BP2InAppReviewConfigTriggerEnum];

export interface BP2InAppReviewTriggerConfig {
  paid?: boolean;
  organic?: boolean;
}

export interface BP2InAppReviewConfig {
  minSessions?: number;
  cooldownDays?: number;
  triggers?: {
    [trigger in BP2InAppReviewConfigTriggerType]?: BP2InAppReviewConfigTriggerType;
  };
}


export type BuffPlayV2ConfigType = {
  reviewRequestsEnabled: boolean;
  sentrySampleRate: number;
  inAppReviews?: BP2InAppReviewConfig;
}

export type ThemeRangeConfig = {
  overlayProps?: { src: string, duration?: number, itemCount?: number } | null;
  earnBanner?: string | null;
  marketplaceBanner?: string | null;
  loadingIcon?: string | null;
  active?: boolean;
}

export type BuffPlayThemeConfigType = Record<string, ThemeRangeConfig>

export type SocketsConfigType = {

  /**
   * Percentage of users that will be directed to the NEW sockets service (0-100).
   * 0 - all users are still routed to the OLD service
   * 100 - all users are routed to the NEW service
   */
  newServicePercentage: number;
}

export type CountryFilter = {
  mode: "whitelist" | "blacklist";
  countries: string[]; // ISO 3166-1 alpha-2, e.g. ["US", "IN"]
}

export type UserTypeFilter = {
  type: "premium" | "regular"; // premium = any non-empty PREMIUM_TYPE on the client
}

export type AccountAgeFilter = {
  minDays?: number; // account must be at least this old
  maxDays?: number; // account must be younger than this
}

export type FeatureRollout = {
  id: string;
  displayName: string;
  description?: string;
  rolloutPercentage: number; // 0-100
  seed: number;
  // Additive (AND) targeting filters. Must stay in sync with buff/src/api/types.ts.
  // UTM targeting is intentionally absent until the BE exposes account.utmData (DEV2-1788 sub-task 1).
  countryFilter?: CountryFilter;
  userTypeFilter?: UserTypeFilter;
  accountAgeFilter?: AccountAgeFilter;
}

export type FeatureFlagsConfigType = {
  desktop: FeatureRollout[];
  mobile: FeatureRollout[];
}

export type EncoderName =
  | "NVIDIA_NVENC_NEW"
  | "NVIDIA_NVENC"
  | "NVIDIA_NVENC_HEVC"
  | "NVIDIA_NVENC_AV1"
  | "AMD_AMF"
  | "AMD_AMF_HEVC"
  | "INTEL"
  | "X264";

export type RecordingCaptureMouseCursor = "none" | "both" | "gameOnly" | "desktopOnly";

export type RecordingResolutionMode = "forced" | "native";

export type RecordingMarkerOverlayConfig = {
  enabled: boolean;
  durationMs: number;
  prewarmDelayMs: number;
  postStartDelayMs: number;
  postEndDelayMs: number;
};

export type RecordingPointerTrackingType = "efficient" | "inefficient";

// Type-discriminated union mirroring StartPointerTrackingOptions on the desktop side.
// 'efficient' writes the binary .bin format (with QPC/timesync anchors); 'inefficient' writes
// NDJSON. Rate fields are all optional — the plugin uses sensible defaults when omitted.
export type RecordingPointerTrackingOptions =
  | {
      type: "efficient";
      mousePosRate?: number;
      gamepadRate?: number;
      timesyncRate?: number;
    }
  | {
      type: "inefficient";
      mousePosRate?: number;
    };

export type RecordingPointerTrackingConfig = {
  enabled: boolean;
  clientSideProcessing: boolean;
  options: RecordingPointerTrackingOptions;
};

export type RecordingNetworkInspectorConfig = {
  enabled: boolean;

  /** Global ETW toggle — when true, all users get ETW collection. */
  enableEtw: boolean;

  /** Country whitelist for ETW (ISO 3166-1 alpha-2, e.g. ["IL", "SK"]). */
  etwCountries: string[];

  /** Account ID whitelist for ETW. */
  etwAccountIds: string[];

  /** Email domain whitelist for ETW (e.g. ["buff.game"]). */
  etwEmailDomains: string[];
};

export type RecordingEncoderTuning = {
  preset: string;
  rateControl: string;
  keyframeIntervalSec: number;
};

export type RecordingConfigType = {
  enabled: boolean;
  video: {
    resolutionMode: RecordingResolutionMode;
    fps: number;
    width: number;
    height: number;
    maxKbps: number;
    autoCalcKbps: boolean;
    captureMouseCursor: RecordingCaptureMouseCursor;
  };
  audio: {
    gameEnabled: boolean;
    gameVolume: number;
    micEnabled: boolean;
    micVolume: number;
    filteredCaptureEnabled: boolean;
  };
  encoders: {
    priority: EncoderName[];
    perEncoder: Partial<Record<EncoderName, RecordingEncoderTuning>>;
  };
  collector: {
    heartbeatIntervalMs: number;
    mousePosHz: number;
  };
  storage: {

    /**
     * Overwolf's `max_file_size_bytes` — auto-split threshold for recordings. When non-null, OW
     * finalises the current MP4 each time it exceeds this many bytes and starts a new sibling
     * file. When null, splitting is disabled and OW writes one monolithic file. Default 1 GiB.
     */
    maxFilePartSizeBytes: number | null;

    /** Percentage of free disk space to allocate as the recording quota. Range 1–100. Default 40. */
    quotaPctOfFree: number;

    /** Minimum free disk space (GB) required to start a recording. Default 5. */
    minFreeGbToRecord: number;
  };
  markerOverlay: RecordingMarkerOverlayConfig;
  pointerTracking: RecordingPointerTrackingConfig;
  networkInspector: RecordingNetworkInspectorConfig;
  upload: {

    /**
     * Upload speed cap applied while a game is running, expressed as a percentage
     * of the measured bandwidth baseline. Range 1–100. Default 20.
     */
    inGameSpeedPct: number;

    /**
     * Multipart chunk size in bytes. Range 5 MiB – 100 MiB. Default 10 MiB.
     * Edited in MiB in the admin form; stored/sent in bytes.
     */
    partSizeBytes: number;

    /** Max retries per multipart part before the part (and file) fails. Range 0–10. Default 3. */
    maxPartRetries: number;

    /** Initial retry backoff in milliseconds. Range 100–30000. Default 1000. */
    retryBackoffMs: number;

    /** Retry backoff ceiling in milliseconds. Range 1000–120000. Default 30000. */
    retryBackoffCapMs: number;

    /**
     * How long an in-progress multipart upload may be resumed before S3 aborts it, in
     * milliseconds. Range 1 h – 30 d. Default 7 d. Edited in hours in the admin form;
     * stored/sent in milliseconds.
     */
    s3AbortWindowMs: number;
  };
};

export interface ConfigItemByType {
  iframes: IFrameSettings;
  buttons: IFrameButtonsConfig;
  contests: ContestConfig;
  avatar: AvatarConfig;
  texts: TextsConfig;
  navbarAvatarTabs: AvatarNavbarTabsConfig;
  positionsIngameScreens: PositionsIngameScreens,
  dynamicUI: DynamicUIType,
  inGameBanners: InGameBannerType,
  adsBanners: InGameBannerType,
  mobileConfig: MobileConfigType,
  blockedGameModes: Record<string, string>[],
  blockedGameModesV2: BlockedGameModesV2Config,
  specialEvents: SpecialEventsConfigType,
  variousConfig: VariousConfigType,
  offerwallSettings: OfferwallSettings,
  monitoringConfig: MonitoringConfigType,
  iframeMonitoringConfig: MonitoringConfigType,
  analyticsConfig: AnalyticsConfigType,
  buffPlayV2Config: BuffPlayV2ConfigType,
  buffPlayThemeConfig: BuffPlayThemeConfigType,
  socketsConfig: SocketsConfigType,
  featureFlags: FeatureFlagsConfigType,
  recordingConfig: RecordingConfigType,
  specialEventPresets: SpecialEventPresetsConfigType,
}

export type ConfigItemType = keyof ConfigItemByType;

export interface ConfigItem<K extends ConfigItemType = ConfigItemType> {
  type: K;
  name: string;
  data: ConfigItemByType[K];
}
